import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ArrowLeft, ShieldCheck, Loader2, CheckCircle2, AlertCircle, Clock, ScanFace, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';
import { idvMeta, IDV_RETRY } from '../../lib/identityStatus';

// Didit status → badge meta for the client view.
const STATUS_META = {
  'Approved':    { label: 'Verified', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  'In Review':   { label: 'In review', color: 'bg-amber-100 text-amber-800', icon: Clock },
  'In Progress': { label: 'In progress', color: 'bg-blue-100 text-blue-800', icon: Clock },
  'Awaiting User': { label: 'Action needed', color: 'bg-amber-100 text-amber-800', icon: Clock },
  'Declined':    { label: 'Declined', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  'Expired':     { label: 'Expired', color: 'bg-muted text-muted-foreground', icon: AlertCircle },
  'Kyc Expired': { label: 'Expired', color: 'bg-muted text-muted-foreground', icon: AlertCircle },
  'Abandoned':   { label: 'Not finished', color: 'bg-muted text-muted-foreground', icon: AlertCircle },
  'Not Started': { label: 'Not started', color: 'bg-muted text-muted-foreground', icon: ShieldCheck },
};

export default function IdentityVerificationForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await onboardingAPI.identityStatus(clientId);
      setStatus(res.data);
    } catch { /* none yet */ } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const launch = async () => {
    setLaunching(true);
    try {
      const res = await onboardingAPI.createIdentitySession(clientId, window.location.origin);
      const url = res.data?.verification_url;
      if (!url) { toast.error('Could not start verification — please try again.'); setLaunching(false); return; }
      // Full-page redirect to the Didit hosted flow; Didit returns the user to
      // /onboarding/section/income_sources via the callback we set server-side.
      window.location.href = url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start identity verification');
      setLaunching(false);
    }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  const didit = status?.didit_status || 'Not Started';
  const meta = { ...(STATUS_META[didit] || STATUS_META['Not Started']), ...idvMeta(didit, status?.id_verification_status, status?.id_verification_manual) };
  const StatusIcon = meta.icon || ShieldCheck;
  const verified = meta.ok;
  const inFlight = !verified && ['In Progress', 'In Review', 'Awaiting User', 'Resubmitted'].includes(didit);
  const retry = !verified && IDV_RETRY.includes(didit);
  const canRetry = !verified && (retry || didit === 'Not Started' || !status?.didit_status);

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <ScanFace className="w-6 h-6" /> Identity Verification
          </CardTitle>
          <CardDescription>
            Identity verification is required before you can submit your onboarding. To keep your account secure and meet our compliance obligations, we need to verify your identity.
            You'll be taken to our verification partner to scan your ID and take a quick selfie. It takes about two minutes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Current status */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <span className="flex items-center gap-2 text-foreground/90">
              <StatusIcon className="w-5 h-5 text-foreground" /> Verification status
            </span>
            <Badge className={meta.color} data-testid="identity-status">{meta.label}</Badge>
          </div>

          {verified && (
            <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{meta.detail ? 'Your identity has been verified by your accountant.' : "Your identity has been verified. You're all set — continue with the rest of your onboarding."}</span>
            </div>
          )}

          {inFlight && (
            <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>Your verification is {didit === 'In Review' ? 'being reviewed' : 'in progress'}. You can keep filling in the other sections, but you can only submit once your identity is approved. If you closed the verification window, resume it below.</span>
            </div>
          )}

          {retry && (
            <div className="flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="identity-retry-msg">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{didit === 'Declined' ? 'Your verification was declined.' : 'Your verification session expired before it was finished.'} Please start a new verification. If you cannot verify online, contact your accountant.</span>
            </div>
          )}

          {/* What we'll check */}
          <div className="rounded-lg bg-muted/50 border p-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">What you'll need</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>A valid government photo ID (passport or driver's licence)</li>
              <li>Your device camera for a quick liveness selfie</li>
            </ul>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
            <ShieldCheck className="w-3.5 h-3.5" /> Your documents are handled securely by our verification partner.
          </div>

          {/* Actions */}
          <div className="flex flex-wrap justify-between gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => navigate('/onboarding/section/income_sources')} data-testid="identity-continue">
              Continue to Income Sources <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            {(canRetry || inFlight) && (
              <Button onClick={launch} disabled={launching} data-testid="identity-launch">
                {launching
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
                  : <><ScanFace className="w-4 h-4 mr-2" /> {retry ? 'Start a new verification' : canRetry ? 'Verify my identity' : 'Resume verification'}</>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
